/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useState } from "react";

interface MonsterViolation {
  id: string;
  source: string;     // 'report' | 'rejection'
  reportType: string; // 'violation' | 'copyright' | 'error'
  status: string;     // 'open' | 'confirmed' | 'resolved'
  offensiveType: string;
  description: string;
  createdAt: number | null;
  resolvedAt: number | null;
}

interface MyViolationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  loadViolations: () => Promise<MonsterViolation[]>;
  // Acknowledge ("resolve") one violation; resolves true on success. The owner must clear every
  // outstanding violation before the monster can be shared again (errors need not be resolved).
  onResolve: (violationId: string) => Promise<boolean>;
}

// Owner-facing view of the report(s) and moderator rejection(s) filed against one of their own
// monsters. Reporter identity is stripped server-side; only the reasons are shown. Each outstanding
// violation must be acknowledged via its Resolve button before the monster can be shared again;
// resolved reports show a checkmark. Data issues (errors) are informational and need no action.
const MyViolationsModal: React.FC<MyViolationsModalProps> = ({ isOpen, onClose, loadViolations, onResolve }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [violations, setViolations] = useState<MonsterViolation[]>([]);
  const [resolvingId, setResolvingId] = useState<string>('');

  const reload = () => {
    setLoading(true);
    return loadViolations().then((v) => {
      setViolations(v);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    loadViolations().then((v) => {
      if (!cancelled) {
        setViolations(v);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) return null;

  const isError = (v: MonsterViolation) => v.reportType === 'error';
  const isResolved = (v: MonsterViolation) => v.status === 'resolved' || v.resolvedAt != null;

  // The offensive part of a content violation, read naturally ('both' -> 'image and text').
  const offensiveLabel = (offensiveType: string) =>
    offensiveType === 'both' ? 'image and text'
    : offensiveType === 'image' ? 'image'
    : offensiveType === 'text' ? 'text'
    : '';

  const handleResolve = async (id: string) => {
    setResolvingId(id);
    const ok = await onResolve(id);
    if (ok) {
      await reload();
    }
    setResolvingId('');
  };

  const anyUnresolvedViolation = violations.some(v => !isError(v) && !isResolved(v));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ width: '560px', height: 'auto', minHeight: '260px' }} onClick={(e) => e.stopPropagation()}>
        <span className="modal-title" style={{ display: 'block', textAlign: 'center' }}>Reports Against This Monster</span>
        <div>
          {loading && <p>Loading…</p>}
          {!loading && violations.length === 0 && <p>There are no reports against this monster.</p>}
          {!loading && violations.length > 0 && (
            <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
              {violations.map((v) => (
                <li key={v.id} style={{ marginBottom: '10px', display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                  <div style={{ flexShrink: 0, width: '90px', paddingTop: '2px' }}>
                    {isResolved(v) ? (
                      <span title="Resolved"><input type="checkbox" checked readOnly /> Resolved</span>
                    ) : (
                      <button type="button" disabled={resolvingId === v.id}
                              onClick={() => handleResolve(v.id)}>
                        {resolvingId === v.id ? '…' : 'Resolve'}
                      </button>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    {isError(v)
                      ? <b style={{ color: 'black' }}>Data issue</b>
                      : v.reportType === 'copyright'
                        ? <b style={{ color: 'red' }}>Copyright violation</b>
                        : <b style={{ color: 'red' }}>Violation</b>}
                    {!isError(v) && offensiveLabel(v.offensiveType) ? ` — ${offensiveLabel(v.offensiveType)}` : ''}
                    {v.createdAt ? <span style={{ fontSize: 'small', color: '#555' }}> ({new Date(v.createdAt).toLocaleDateString()})</span> : null}
                    <div>{v.description}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p style={{ fontSize: 'small', fontStyle: 'italic' }}>
            {anyUnresolvedViolation
              ? 'This monster has been set to Private so you can fix it. Revise it, click Resolve on each violation above, then request sharing again — a moderator will re-review it before it becomes visible. (Data issues need no action.)'
              : 'To revise this monster, edit it while it is Private, then request sharing again (a moderator will re-review it).'}
          </p>
        </div>
        <div className="modal-actions" style={{ textAlign: 'center' }}>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default MyViolationsModal;
